import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import ReasonRejectionAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/masters/reason-rejection/reason-rejection.entity';
import { ReasonRejectionService } from '../../reason-for-rejection/reason-rejection.service';
import { IRequestResult } from 'app/common/interfaces';

export interface RejectionReasonSelectorData {
  currentReasonId?: string;
}

@Component({
  selector: 'app-rejection-reason-selector',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule
  ],
  template: `
    <h2 mat-dialog-title class="text-lg font-bold mb-4">Seleccionar Razón de Rechazo</h2>
    <mat-dialog-content class="min-w-96">
      <p class="text-sm text-gray-600 mb-4">
        Seleccione la razón de rechazo que desea vincular con esta condición de cambio.
      </p>
      
      <mat-form-field appearance="fill" class="w-full">
        <mat-label>Buscar razón de rechazo</mat-label>
        <input matInput
               [formControl]="rejectionReasonControl"
               [matAutocomplete]="auto"
               placeholder="Escriba para buscar...">
        <mat-autocomplete #auto="matAutocomplete" 
                         [displayWith]="displayRejectionReason">
          <mat-option *ngFor="let reason of filteredReasons$ | async" 
                     [value]="reason">
            {{ reason.name }}
          </mat-option>
        </mat-autocomplete>
      </mat-form-field>
    </mat-dialog-content>
    
    <mat-dialog-actions class="flex justify-end gap-2 p-4">
      <button mat-button (click)="onCancel()" class="text-gray-600">
        Cancelar
      </button>
      <button mat-raised-button color="primary" 
              (click)="onConfirm()"
              [disabled]="!isValidSelection()">
        Confirmar
      </button>
    </mat-dialog-actions>
  `,
  styleUrls: ['./rejection-reason-selector.component.scss']
})
export class RejectionReasonSelectorComponent implements OnInit {
  rejectionReasonControl = new FormControl();
  rejectionReasons: ReasonRejectionAttributes[] = [];
  filteredReasons$: Observable<ReasonRejectionAttributes[]>;

  constructor(
    private dialogRef: MatDialogRef<RejectionReasonSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RejectionReasonSelectorData,
    private reasonRejectionService: ReasonRejectionService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadRejectionReasons();
  }

  loadRejectionReasons(): void {
    this.reasonRejectionService.findToComboBox().subscribe({
      next: (res: IRequestResult<ReasonRejectionAttributes[]>) => {
        this.rejectionReasons = res.data;
        this.setupFilter();
        this.setCurrentSelection();
      },
      error: (err) => {
        this.snackBar.open('Error al cargar las razones de rechazo', 'Cerrar', { duration: 3000 });
      }
    });
  }

  setupFilter(): void {
    this.filteredReasons$ = this.rejectionReasonControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterReasons(value || ''))
    );
  }

  setCurrentSelection(): void {
    if (this.data?.currentReasonId) {
      const currentReason = this.rejectionReasons.find(
        reason => reason._id?.toString() === this.data.currentReasonId
      );
      if (currentReason) {
        this.rejectionReasonControl.setValue(currentReason);
      }
    }
  }

  private _filterReasons(value: string): ReasonRejectionAttributes[] {
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();
      return this.rejectionReasons.filter(reason =>
        reason.name?.toLowerCase().includes(filterValue)
      );
    }
    return this.rejectionReasons;
  }

  displayRejectionReason(reason: ReasonRejectionAttributes): string {
    return reason && reason.name ? reason.name : '';
  }

  isValidSelection(): boolean {
    const selectedValue = this.rejectionReasonControl.value;
    return selectedValue && selectedValue._id;
  }

  onConfirm(): void {
    const selectedReason = this.rejectionReasonControl.value;
    if (selectedReason && selectedReason._id) {
      this.dialogRef.close(selectedReason._id);
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}